import { z } from "zod";
import { ROLES_GLOBALES, ROLES_PROCESO } from "./dominio.js";
import { esquemaFrecuencia } from "./frecuencia.js";

/**
 * Esquemas Zod de la administración del sistema (rol `administrador`): usuarios, catálogos de
 * procesos y tipos de documento, y el listado de puestos. Como los de `./esquemas.ts`, validan
 * el formulario en la web y la entrada en la API, con mensajes en español.
 */

// --- Usuarios ---------------------------------------------------------------------------------

/** En qué procesos está la persona y con qué rol: punto verde (carga) o gris (consulta). */
const esquemaAccesoAProceso = z.object({
  procesoId: z.string().min(1),
  rol: z.enum(ROLES_PROCESO),
});

/** Un formato de registro que la persona llena, y cada cuánto. */
const esquemaDiligenciaAsignada = z.object({
  documentoId: z.string().min(1),
  frecuencia: esquemaFrecuencia,
});
export type DiligenciaAsignada = z.infer<typeof esquemaDiligenciaAsignada>;

/** Lo que el administrador escribe de una persona. El correo solo al crear: Google entra por él. */
export const esquemaDatosUsuario = z.object({
  nombre: z
    .string()
    .trim()
    .min(3, "Escribe el nombre completo, como va a salir en las firmas")
    .max(120, "El nombre es demasiado largo"),
  /**
   * El puesto del listado aprobado (DIR-ESP-004). Con `puestoId`, la API copia el nombre del
   * catálogo a `puesto`; sin él, vale el texto (personas dadas de alta antes del listado, y el
   * comando de la primera cuenta).
   */
  puestoId: z.string().min(1).nullable().default(null),
  puesto: z
    .string()
    .trim()
    .min(2, "Elige el puesto del listado")
    .max(120, "El puesto es demasiado largo"),
  rolGlobal: z.enum(ROLES_GLOBALES, { error: "Elige un rol" }),
  accesoExterno: z.boolean(),
  /** Permiso temporal de subir sin firmas. Sin el campo, no se toca. */
  cargaSinFirmas: z.boolean().optional(),
  procesos: z.array(esquemaAccesoAProceso).max(50),
  /**
   * Sus diligencias: qué formatos de registro llena y cada cuánto (2026-10-20). Sin el campo, no
   * se tocan; con una lista (aunque vacía), quedan exactamente esas.
   */
  diligencias: z.array(esquemaDiligenciaAsignada).max(100).optional(),
});

export const esquemaCrearUsuario = esquemaDatosUsuario.extend({
  correo: z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email("Escribe un correo válido"))
    .pipe(z.string().max(254, "El correo es demasiado largo")),
});

export const esquemaActualizarUsuario = esquemaDatosUsuario.extend({
  id: z.string().min(1),
});

export const esquemaIdUsuario = z.object({ id: z.string().min(1) });

// --- Catálogos: procesos y tipos de documento ------------------------------------------------

/** `LOG`, `GES`, `TAH`: la parte del código de documento que identifica el proceso o el tipo. */
const esquemaCodigoCatalogo = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2,4}$/, "De dos a cuatro letras, sin números ni guiones (por ejemplo LOG o GES)");

export const esquemaProceso = z.object({
  codigo: esquemaCodigoCatalogo,
  nombre: z.string().trim().min(3, "Escribe el nombre del proceso").max(120),
  /** Quién es el dueño del proceso (aparece en "Mi proceso" de su menú). Opcional. */
  duenoId: z.string().min(1).nullable(),
  activo: z.boolean(),
});
export const esquemaActualizarProceso = esquemaProceso.extend({ id: z.string().min(1) });

export const esquemaTipoDocumento = z.object({
  codigo: esquemaCodigoCatalogo,
  nombre: z.string().trim().min(3, "Escribe el nombre del tipo").max(120),
  esRegistro: z.boolean(),
  tieneRevisionAnual: z.boolean(),
  requiereAprobacion: z.boolean(),
  /**
   * Ruta corta (2026-10-29): false = sus documentos llevan dos firmas, elaboró y aprobó, sin
   * revisor. Solo cuenta si el tipo pasa por la ruta de firmas.
   */
  conRevisor: z.boolean().default(true),
  /** Comunicado para toda la empresa: lo ve y lo recibe todo el personal activo (2026-10-20). */
  paraTodaLaEmpresa: z.boolean().default(false),
  activo: z.boolean(),
});
export const esquemaActualizarTipoDocumento = esquemaTipoDocumento.extend({
  id: z.string().min(1),
});

// --- Puestos (DIR-ESP-004) --------------------------------------------------------------------

/** `CCI-01`, `TIC-01`: el código de un puesto en el listado aprobado DIR-ESP-004. */
const esquemaCodigoPuesto = z
  .string()
  .trim()
  .toUpperCase()
  .regex(
    /^[A-Z]{2,4}-\d{2}$/,
    "Como en el listado: letras del área, guion y dos dígitos (por ejemplo CCI-01)",
  );

export const esquemaPuesto = z.object({
  codigo: esquemaCodigoPuesto,
  nombre: z.string().trim().min(3, "Escribe el nombre del puesto").max(120),
  /** El proceso de la organización tal como lo dice el listado. Opcional. */
  area: z.string().trim().max(160).default(""),
  activo: z.boolean(),
});
export const esquemaActualizarPuesto = esquemaPuesto.extend({ id: z.string().min(1) });
